import type { ApiResult } from './types';
import { caseApiRequest, dataPayloadErrorMessage, isDataPayloadSuccess } from './apiClient';

const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

export type SubCategoryListRow = {
  id: string;
  subCategoryName: string;
  subCategoryCode: string;
  subCategoryActive: boolean;
  orderIndex: number;
  categoryId: string;
  categoryName: string;
  categoryCode: string;
  categoryActive: boolean;
  metaData: boolean;
};

export type SubCategoryCategory = {
  id: string;
  name: string;
  code: string;
  active: boolean;
  orderIndex: number;
};

export type SubCategoryApplicableArea = {
  id: string;
  name: string;
  code: string;
  active: boolean;
  orderIndex: number;
  subCategoryId: string;
};

export type SubCategoryChecklistDocument = {
  id: string;
  active: boolean;
  optional: boolean;
  caseCategoryId: string;
  documentTypeId: string;
  documentTypeName: string;
  documentTypeCode: string;
  documentTypeDescription: string;
};

export type SubCategoryDetail = {
  id: string;
  name: string;
  code: string;
  active: boolean;
  orderIndex: number;
  metaData: boolean;
  category: SubCategoryCategory | null;
  applicableAreas: SubCategoryApplicableArea[];
  checklistDocuments: SubCategoryChecklistDocument[];
};

export type DocumentTypeOption = {
  id: string;
  name: string;
  code: string;
  description: string;
  active: boolean;
};

export type SubCategoryDocumentInput = {
  documentId: string;
  optional?: boolean;
};

export type CreateSubCategoryInput = {
  name: string;
  code: string;
  active: boolean;
  categoryId: string;
  codeAvailable: boolean;
  applicableAreas: string[];
  documents: SubCategoryDocumentInput[];
};

export type UpdateSubCategoryInput = {
  name: string;
  code: string;
  active: boolean;
  applicableAreas: string[];
  documents: SubCategoryDocumentInput[];
};

type ApiListRow = {
  sub_category_id?: string;
  sub_category_name?: string;
  sub_category_code?: string;
  sub_category_active?: boolean;
  order_index?: number;
  category_id?: string;
  category_name?: string;
  category_code?: string;
  category_active?: boolean;
  meta_data?: boolean;
};

type ApiApplicableAreaRow = {
  id?: string;
  name?: string;
  code?: string;
  active?: boolean;
  order_index?: number;
  sub_category_id?: string;
};

type ApiChecklistDocumentRow = {
  id?: string;
  active?: boolean;
  optional?: boolean;
  caseCategoryId?: string;
  case_category_id?: string;
  documentTypeId?: string;
  document_type_id?: string;
  documentTypeName?: string;
  document_type_name?: string;
  documentTypeCode?: string;
  document_type_code?: string;
  documentTypeDescription?: string;
  document_type_description?: string;
};

type ApiDetailRow = {
  id?: string;
  name?: string;
  code?: string;
  active?: boolean;
  order_index?: number;
  meta_data?: boolean;
  sub_category_id?: string;
  sub_category_name?: string;
  sub_category_code?: string;
  sub_category_active?: boolean;
  category_id?: string;
  category_name?: string;
  category_code?: string;
  category_active?: boolean;
  category?: {
    id?: string;
    name?: string;
    code?: string;
    active?: boolean;
    order_index?: number;
  };
  applicable_areas?: ApiApplicableAreaRow[];
  checklist_documents?: ApiChecklistDocumentRow[];
  documents?: ApiChecklistDocumentRow[];
};

type ApiDocumentTypeRow = {
  id?: string;
  name?: string;
  code?: string;
  description?: string;
  active?: boolean;
};

function mapListRow(row: ApiListRow): SubCategoryListRow | null {
  const id = str(row.sub_category_id);
  const subCategoryName = str(row.sub_category_name);
  if (!id || !subCategoryName) return null;
  return {
    id,
    subCategoryName,
    subCategoryCode: str(row.sub_category_code),
    subCategoryActive: row.sub_category_active !== false,
    orderIndex: typeof row.order_index === 'number' ? row.order_index : 0,
    categoryId: str(row.category_id),
    categoryName: str(row.category_name),
    categoryCode: str(row.category_code),
    categoryActive: row.category_active !== false,
    metaData: Boolean(row.meta_data),
  };
}

function mapApplicableArea(row: ApiApplicableAreaRow): SubCategoryApplicableArea | null {
  const id = str(row.id);
  const name = str(row.name);
  if (!id || !name) return null;
  return {
    id,
    name,
    code: str(row.code),
    active: row.active !== false,
    orderIndex: typeof row.order_index === 'number' ? row.order_index : 0,
    subCategoryId: str(row.sub_category_id),
  };
}

function mapChecklistDocument(row: ApiChecklistDocumentRow): SubCategoryChecklistDocument | null {
  const id = str(row.id);
  const documentTypeId = str(row.documentTypeId ?? row.document_type_id);
  if (!id || !documentTypeId) return null;
  return {
    id,
    active: row.active !== false,
    optional: Boolean(row.optional),
    caseCategoryId: str(row.caseCategoryId ?? row.case_category_id),
    documentTypeId,
    documentTypeName: str(row.documentTypeName ?? row.document_type_name),
    documentTypeCode: str(row.documentTypeCode ?? row.document_type_code),
    documentTypeDescription: str(row.documentTypeDescription ?? row.document_type_description),
  };
}

function mapDetail(row: ApiDetailRow): SubCategoryDetail | null {
  const r = row as Record<string, unknown>;
  const id = str(row.id ?? r.sub_category_id);
  const name = str(row.name ?? r.sub_category_name);
  if (!id || !name) return null;
  const cat = row.category;
  const categoryId = str(cat?.id ?? r.category_id);
  const categoryName = str(cat?.name ?? r.category_name);
  return {
    id,
    name,
    code: str(row.code ?? r.sub_category_code),
    active: (row.active ?? r.sub_category_active) !== false,
    orderIndex: typeof row.order_index === 'number' ? row.order_index : 0,
    metaData: Boolean(row.meta_data),
    category: categoryId
      ? {
          id: categoryId,
          name: categoryName,
          code: str(cat?.code ?? r.category_code),
          active: (cat?.active ?? r.category_active) !== false,
          orderIndex: typeof cat?.order_index === 'number' ? cat.order_index : 0,
        }
      : null,
    applicableAreas: (row.applicable_areas ?? [])
      .map(mapApplicableArea)
      .filter((x): x is SubCategoryApplicableArea => x != null),
    checklistDocuments: (row.checklist_documents ?? row.documents ?? [])
      .map(mapChecklistDocument)
      .filter((x): x is SubCategoryChecklistDocument => x != null),
  };
}

function mapDocumentType(row: ApiDocumentTypeRow): DocumentTypeOption | null {
  const id = str(row.id);
  const name = str(row.name);
  if (!id || !name) return null;
  return {
    id,
    name,
    code: str(row.code),
    description: str(row.description),
    active: row.active !== false,
  };
}

function buildDocumentsPayload(documents: SubCategoryDocumentInput[]) {
  return documents.map((d) => ({
    document_id: d.documentId,
    optional: d.optional ?? false,
  }));
}

/** CMS-MCRSC-2 — list all sub categories with optional filters */
export async function listAllSubCategories(input?: {
  categoryId?: string;
  subCategoryId?: string;
}): Promise<ApiResult<SubCategoryListRow[]>> {
  const qs = new URLSearchParams();
  const categoryId = input?.categoryId?.trim();
  const subCategoryId = input?.subCategoryId?.trim();
  if (categoryId) qs.set('category_id', categoryId);
  if (subCategoryId) qs.set('sub_category_id', subCategoryId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await caseApiRequest<ApiListRow[]>(`/case-request-sub-categories/all${suffix}`);
  if (!res.ok) return { success: false, message: res.message };
  const rows = (Array.isArray(res.data) ? res.data : [])
    .map(mapListRow)
    .filter((x): x is SubCategoryListRow => x != null);
  return { success: true, data: rows };
}

/** CMS-MCRSC-3 — sub category detail */
export async function getSubCategoryById(subCategoryId: string): Promise<ApiResult<SubCategoryDetail>> {
  const id = subCategoryId.trim();
  if (!id) return { success: false, message: 'Sub category id is required.' };
  const res = await caseApiRequest<ApiDetailRow>(`/case-request-sub-categories/${encodeURIComponent(id)}`);
  if (!res.ok) return { success: false, message: res.message };
  const detail = mapDetail(res.data ?? {});
  if (!detail) return { success: false, message: 'Invalid sub category response.' };
  return { success: true, data: detail };
}

/** CMS-MDT-1 — active document types */
export async function listDocumentTypes(): Promise<ApiResult<DocumentTypeOption[]>> {
  const res = await caseApiRequest<ApiDocumentTypeRow[]>('/document-types');
  if (!res.ok) return { success: false, message: res.message };
  const rows = (Array.isArray(res.data) ? res.data : [])
    .map(mapDocumentType)
    .filter((x): x is DocumentTypeOption => x != null && x.active);
  rows.sort((a, b) => a.name.localeCompare(b.name));
  return { success: true, data: rows };
}

/** CMS-MCRSC-4 — create sub category */
export async function createSubCategory(input: CreateSubCategoryInput): Promise<ApiResult<SubCategoryDetail>> {
  const res = await caseApiRequest<ApiDetailRow>('/case-request-sub-categories', {
    method: 'POST',
    body: {
      name: input.name.trim(),
      code: input.code.trim(),
      active: input.active,
      category_id: input.categoryId.trim(),
      code_available: input.codeAvailable,
      applicable_areas: input.applicableAreas.map((a) => a.trim()).filter(Boolean),
      documents: buildDocumentsPayload(input.documents),
    },
  });
  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) {
    return { success: false, message: dataPayloadErrorMessage(res.data) };
  }
  const detail = mapDetail(res.data ?? {});
  if (!detail) return { success: false, message: 'Invalid create response.' };
  return { success: true, data: detail };
}

/** CMS-MCRSC-5 — update sub category */
export async function updateSubCategory(
  subCategoryId: string,
  input: UpdateSubCategoryInput,
): Promise<ApiResult<SubCategoryDetail>> {
  const id = subCategoryId.trim();
  if (!id) return { success: false, message: 'Sub category id is required.' };
  const qs = new URLSearchParams({ sub_category_id: id });
  const res = await caseApiRequest<ApiDetailRow>(`/case-request-sub-categories?${qs.toString()}`, {
    method: 'PUT',
    body: {
      name: input.name.trim(),
      code: input.code.trim(),
      active: input.active,
      applicable_areas: input.applicableAreas.map((a) => a.trim()).filter(Boolean),
      documents: buildDocumentsPayload(input.documents),
    },
  });
  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) {
    return { success: false, message: dataPayloadErrorMessage(res.data) };
  }
  const detail = mapDetail(res.data ?? {});
  if (!detail) return { success: false, message: 'Invalid update response.' };
  return { success: true, data: detail };
}

/** Parse newline-separated applicable area labels from a textarea. */
export function parseApplicableAreasText(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

/** Join applicable area names for display in a textarea. */
export function formatApplicableAreasText(areas: SubCategoryApplicableArea[]): string {
  return areas.map((a) => a.name).join('\n');
}
